// src/pages/AuditLogs.tsx
import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import {
  Shield,
  Filter,
  Download,
  Eye,
  User,
  Activity,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  BarChart3,
  Users,
  RefreshCw,
} from "lucide-react";
import { auditService } from "../services/audit";
import Input from "../components/common/Input";
import Select from "../components/common/Select";
import Card from "../components/common/Card";
import {
  AuditLog,
  AuditLogFilters,
  AuditLogSummary,
  AuditFilterOptions,
} from "../types/audit";
import { useHorizontalDragScroll } from "../hooks/useHorizontalDragScroll";

const AuditLogsPage: React.FC = () => {
  const tableScrollRef = useHorizontalDragScroll<HTMLDivElement>();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("logs");

  // Data states
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [summary, setSummary] = useState<AuditLogSummary | null>(null);
  const [filterOptions, setFilterOptions] = useState<AuditFilterOptions | null>(
    null
  );
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 50,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  // Filter states
  const [filters, setFilters] = useState<AuditLogFilters>({
    page: 1,
    limit: 50,
    sortBy: "timestamp",
    sortOrder: -1,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

  const tabs = [
    { id: "logs", label: "Audit Logs", icon: Activity },
    { id: "dashboard", label: "Dashboard", icon: BarChart3 },
    { id: "users", label: "User Activity", icon: Users },
    { id: "security", label: "Security Events", icon: Shield },
  ];

  useEffect(() => {
    loadData();
    loadFilterOptions();
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);

      if (activeTab === "logs") {
        const response = await auditService.getAuditLogs(filters);
        setAuditLogs(response.logs);
        setPagination(response.pagination);
      } else if (activeTab === "dashboard") {
        const summaryResponse = await auditService.getAuditSummary(30);
        setSummary(summaryResponse);
      }
    } catch (error: any) {
      console.error("Error loading audit data:", error);
      toast.error(
        "Failed to load audit data: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setLoading(false);
    }
  };

  const loadFilterOptions = async () => {
    try {
      const response = await auditService.getFilterOptions();
      setFilterOptions(response);
    } catch (error) {
      console.error("Error loading filter options:", error);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters((prev) => ({ ...prev, page: 1, search: searchTerm }));
  };

  const handleFilterChange = (key: keyof AuditLogFilters, value: any) => {
    setFilters((prev) => ({ ...prev, page: 1, [key]: value }));
  };

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  const handleExport = async () => {
    try {
      const blob = await auditService.exportAuditLogs({
        startDate: filters.startDate,
        endDate: filters.endDate,
        format: "csv",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = `audit-logs-${new Date().toISOString().split("T")[0]}.csv`;
      
      try {
        document.body.appendChild(a);
        a.click();
      } finally {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }

      toast.success("Audit logs exported successfully");
    } catch (error: any) {
      toast.error(
        "Failed to export audit logs: " +
          (error.response?.data?.message || error.message)
      );
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "CRITICAL":
        return "bg-gradient-to-r from-red-300 to-rose-400 text-white";
      case "HIGH":
        return "bg-gradient-to-r from-orange-300 to-amber-400 text-white";
      case "MEDIUM":
        return "bg-gradient-to-r from-yellow-300 to-amber-400 text-white";
      case "LOW":
        return "bg-gradient-to-r from-emerald-300 to-green-400 text-white";
      default:
        return "bg-gradient-to-r from-slate-300 to-gray-400 text-white";
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "SECURITY":
        return "bg-gradient-to-r from-red-300 to-rose-400 text-white";
      case "AUTHENTICATION":
        return "bg-gradient-to-r from-blue-300 to-indigo-400 text-white";
      case "AUTHORIZATION":
        return "bg-gradient-to-r from-purple-300 to-indigo-400 text-white";
      case "DATA_CHANGE":
        return "bg-gradient-to-r from-emerald-300 to-green-400 text-white";
      case "SYSTEM":
        return "bg-gradient-to-r from-slate-300 to-gray-400 text-white";
      default:
        return "bg-gradient-to-r from-slate-300 to-gray-400 text-white";
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString(),
    };
  };

  if (loading && !auditLogs.length && !summary) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-96">
            <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-12 text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-slate-200 border-t-blue-500 mx-auto mb-6"></div>
              <span className="text-xl font-bold text-slate-900 block">
                Loading audit data...
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-gradient-to-br from-purple-300 to-indigo-400 rounded-2xl p-4 shadow-lg">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-slate-900">
                  Audit Logs
                </h1>
                <p className="text-slate-600 font-semibold mt-1">
                  Monitor system activities, security events, and user actions
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`bg-white border-2 border-slate-200 rounded-xl px-6 py-3 font-bold text-slate-700 hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2 ${
                  showFilters ? "ring-2 ring-blue-400" : ""
                }`}
              >
                <Filter className="w-5 h-5 text-purple-500" />
                Filters
              </button>
              <button
                onClick={handleExport}
                className="bg-white border-2 border-slate-200 rounded-xl px-6 py-3 font-bold text-slate-700 hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2"
              >
                <Download className="w-5 h-5 text-blue-500" />
                Export
              </button>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl mb-8 overflow-hidden">
          <nav className="flex">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setFilters((prev) => ({ ...prev, page: 1 }));
                }}
                className={`flex-1 flex items-center justify-center gap-2 py-4 px-4 font-bold text-sm transition-all duration-300 ${
                  activeTab === tab.id
                    ? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg"
                    : "text-slate-600 hover:bg-gradient-to-r hover:from-slate-50 hover:to-blue-50"
                }`}
              >
                <tab.icon className="w-5 h-5" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Search and Filters */}
        {activeTab === "logs" && (
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
            {/* Search Bar */}
            <form onSubmit={handleSearch} className="flex gap-4 mb-6">
              <div className="flex-1">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search audit logs..."
                  className="w-full px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200/60 rounded-xl font-semibold text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                />
              </div>
              <button
                type="submit"
                className="px-8 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl font-bold hover:shadow-lg hover:scale-105 transition-all duration-300"
              >
                Search
              </button>
            </form>

            {/* Advanced Filters */}
            {showFilters && filterOptions && (
              <div className="bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200/60 p-6 rounded-2xl space-y-4 shadow-sm">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-slate-900 mb-2">
                      Action
                    </label>
                    <select
                      value={filters.action || ""}
                      onChange={(e) =>
                        handleFilterChange(
                          "action",
                          e.target.value || undefined
                        )
                      }
                      className="w-full px-4 py-3 bg-white border-2 border-slate-200/60 rounded-xl font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                    >
                      <option value="">All Actions</option>
                      {filterOptions.actions.map((action) => (
                        <option key={action} value={action}>
                          {action.replace(/_/g, " ")}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-slate-900 mb-2">
                      Category
                    </label>
                    <select
                      value={filters.category || ""}
                      onChange={(e) =>
                        handleFilterChange(
                          "category",
                          e.target.value || undefined
                        )
                      }
                      className="w-full px-4 py-3 bg-white border-2 border-slate-200/60 rounded-xl font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                    >
                      <option value="">All Categories</option>
                      {filterOptions.categories.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-slate-900 mb-2">
                      Severity
                    </label>
                    <select
                      value={filters.severity || ""}
                      onChange={(e) =>
                        handleFilterChange(
                          "severity",
                          e.target.value || undefined
                        )
                      }
                      className="w-full px-4 py-3 bg-white border-2 border-slate-200/60 rounded-xl font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                    >
                      <option value="">All Severities</option>
                      {filterOptions.severities.map((severity) => (
                        <option key={severity} value={severity}>
                          {severity}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-slate-900 mb-2">
                      Status
                    </label>
                    <select
                      value={
                        filters.success !== undefined
                          ? filters.success.toString()
                          : ""
                      }
                      onChange={(e) =>
                        handleFilterChange(
                          "success",
                          e.target.value === ""
                            ? undefined
                            : e.target.value === "true"
                        )
                      }
                      className="w-full px-4 py-3 bg-white border-2 border-slate-200/60 rounded-xl font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                    >
                      <option value="">All</option>
                      <option value="true">Success</option>
                      <option value="false">Failed</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-slate-900 mb-2">
                      Start Date
                    </label>
                    <input
                      type="datetime-local"
                      value={filters.startDate || ""}
                      onChange={(e) =>
                        handleFilterChange(
                          "startDate",
                          e.target.value || undefined
                        )
                      }
                      className="w-full px-4 py-3 bg-white border-2 border-slate-200/60 rounded-xl font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-slate-900 mb-2">
                      End Date
                    </label>
                    <input
                      type="datetime-local"
                      value={filters.endDate || ""}
                      onChange={(e) =>
                        handleFilterChange(
                          "endDate",
                          e.target.value || undefined
                        )
                      }
                      className="w-full px-4 py-3 bg-white border-2 border-slate-200/60 rounded-xl font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setFilters({
                        page: 1,
                        limit: 50,
                        sortBy: "timestamp",
                        sortOrder: -1,
                      });
                      setSearchTerm("");
                    }}
                    className="px-6 py-3 bg-white border-2 border-slate-200 rounded-xl font-bold text-slate-700 hover:shadow-lg hover:scale-105 transition-all duration-300"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tab Content */}
        <div className="space-y-6">
          {loading && (
            <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
              <div className="flex items-center justify-center py-12">
                <div className="flex items-center gap-3 text-slate-600">
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  <span className="font-bold">Loading audit data...</span>
                </div>
              </div>
            </div>
          )}

          {activeTab === "logs" && !loading && (
            <AuditLogsTab
              logs={auditLogs}
              pagination={pagination}
              onPageChange={handlePageChange}
              onViewDetails={setSelectedLog}
              getSeverityColor={getSeverityColor}
              getCategoryColor={getCategoryColor}
              formatTimestamp={formatTimestamp}
            />
          )}

          {activeTab === "dashboard" && !loading && summary && (
            <DashboardTab summary={summary} />
          )}

          {activeTab === "users" && !loading && <UserActivityTab />}

          {activeTab === "security" && !loading && <SecurityEventsTab />}
        </div>

        {/* Log Details Modal */}
        {selectedLog && (
          <LogDetailsModal
            log={selectedLog}
            onClose={() => setSelectedLog(null)}
            getSeverityColor={getSeverityColor}
            getCategoryColor={getCategoryColor}
            formatTimestamp={formatTimestamp}
          />
        )}
      </div>
    </div>
  );
};

// Audit Logs Tab Component
const AuditLogsTab: React.FC<{
  logs: AuditLog[];
  pagination: any;
  onPageChange: (page: number) => void;
  onViewDetails: (log: AuditLog) => void;
  getSeverityColor: (severity: string) => string;
  getCategoryColor: (category: string) => string;
  formatTimestamp: (timestamp: string) => { date: string; time: string };
}> = ({
  logs,
  pagination,
  onPageChange,
  onViewDetails,
  getSeverityColor,
  getCategoryColor,
  formatTimestamp,
}) => {
  const tableScrollRef = useHorizontalDragScroll<HTMLDivElement>();
  
  return (
    <div className="space-y-6">
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl overflow-hidden">
        <div ref={tableScrollRef} className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-gradient-to-r from-slate-50 to-blue-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                  Action
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                  Resource
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                  Severity
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {logs.map((log) => {
                const timestamp = formatTimestamp(log.timestamp);
                return (
                  <tr
                    key={log._id}
                    className="hover:bg-gradient-to-r hover:from-slate-50 hover:to-blue-50 transition-colors duration-200"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-slate-400" />
                        <div>
                          <div className="font-semibold">{timestamp.date}</div>
                          <div className="text-slate-500 text-xs">
                            {timestamp.time}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-slate-400" />
                        <div>
                          <div className="text-sm font-bold text-slate-900">
                            {log.userName}
                          </div>
                          <div className="text-sm text-slate-500">
                            {log.userEmail}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900">
                      {log.action.replace(/_/g, " ")}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      <div>
                        <div className="font-bold">{log.resourceType}</div>
                        {log.resourceName && (
                          <div className="text-slate-500 text-xs">
                            {log.resourceName}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-3 py-1 text-xs font-bold rounded-xl shadow-md ${getCategoryColor(
                          log.metadata.category
                        )}`}
                      >
                        {log.metadata.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {log.success ? (
                          <CheckCircle className="w-5 h-5 text-emerald-500" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-500" />
                        )}
                        <span
                          className={`font-bold ${
                            log.success ? "text-emerald-700" : "text-red-700"
                          }`}
                        >
                          {log.success ? "Success" : "Failed"}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-3 py-1 text-xs font-bold rounded-xl shadow-md ${getSeverityColor(
                          log.metadata.severity
                        )}`}
                      >
                        {log.metadata.severity}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => onViewDetails(log)}
                        className="p-2 bg-gradient-to-br from-blue-300 to-indigo-400 text-white rounded-xl hover:scale-110 transition-all duration-300 shadow-md"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="bg-gradient-to-r from-slate-50 to-blue-50 px-6 py-4 flex items-center justify-between border-t border-slate-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => onPageChange(pagination.currentPage - 1)}
                disabled={!pagination.hasPreviousPage}
                className="relative inline-flex items-center px-4 py-2 border-2 border-slate-200 text-sm font-bold rounded-xl text-slate-700 bg-white hover:shadow-lg hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 transition-all duration-300"
              >
                Previous
              </button>
              <button
                onClick={() => onPageChange(pagination.currentPage + 1)}
                disabled={!pagination.hasNextPage}
                className="ml-3 relative inline-flex items-center px-4 py-2 border-2 border-slate-200 text-sm font-bold rounded-xl text-slate-700 bg-white hover:shadow-lg hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 transition-all duration-300"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-bold text-slate-700">
                  Showing{" "}
                  <span className="font-bold text-slate-900">
                    {(pagination.currentPage - 1) * pagination.itemsPerPage + 1}
                  </span>{" "}
                  to{" "}
                  <span className="font-bold text-slate-900">
                    {Math.min(
                      pagination.currentPage * pagination.itemsPerPage,
                      pagination.totalItems
                    )}
                  </span>{" "}
                  of{" "}
                  <span className="font-bold text-slate-900">
                    {pagination.totalItems}
                  </span>{" "}
                  results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-xl shadow-sm gap-2">
                  {Array.from(
                    { length: Math.min(pagination.totalPages, 10) },
                    (_, i) => {
                      let page;
                      if (pagination.totalPages <= 10) {
                        page = i + 1;
                      } else if (pagination.currentPage <= 5) {
                        page = i + 1;
                      } else if (
                        pagination.currentPage >=
                        pagination.totalPages - 4
                      ) {
                        page = pagination.totalPages - 9 + i;
                      } else {
                        page = pagination.currentPage - 5 + i;
                      }

                      return (
                        <button
                          key={page}
                          onClick={() => onPageChange(page)}
                          className={`relative inline-flex items-center px-4 py-2 text-sm font-bold rounded-xl transition-all duration-300 ${
                            page === pagination.currentPage
                              ? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg"
                              : "bg-white border-2 border-slate-200 text-slate-700 hover:shadow-lg hover:scale-105"
                          }`}
                        >
                          {page}
                        </button>
                      );
                    }
                  )}
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {logs.length === 0 && (
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-16">
          <div className="text-center">
            <div className="bg-gradient-to-br from-slate-300 to-gray-400 rounded-2xl p-8 shadow-lg inline-block mb-6">
              <Activity className="w-16 h-16 text-white" />
            </div>
            <p className="text-slate-900 text-xl font-bold mb-2">
              No audit logs found
            </p>
            <p className="text-slate-600 font-semibold">
              Try adjusting your filters or search terms
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// Dashboard Tab Component
const DashboardTab: React.FC<{ summary: AuditLogSummary }> = ({ summary }) => {
  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {summary.summary.map((item) => (
          <div
            key={item._id}
            className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:scale-105 transition-all duration-300"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-br from-blue-300 to-indigo-400 rounded-2xl shadow-lg">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="text-3xl font-bold text-slate-900">
                  {item.total}
                </div>
                <div className="text-sm font-bold text-slate-600">
                  {item._id} Events
                </div>
                <div className="text-xs font-semibold text-slate-500">
                  {item.successful} success, {item.failed} failed
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Top Users */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl overflow-hidden">
        <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-blue-50">
          <h3 className="text-xl font-bold text-slate-900">
            Most Active Users
          </h3>
        </div>
        <div className="p-6">
          <div className="space-y-3">
            {summary.topUsers.slice(0, 10).map((user, index) => (
              <div
                key={user._id}
                className="flex items-center justify-between p-3 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-300 to-indigo-400 rounded-xl flex items-center justify-center text-sm font-bold text-white shadow-md">
                    {index + 1}
                  </div>
                  <div className="text-sm font-bold text-slate-900">
                    {user.userName}
                  </div>
                </div>
                <div className="text-sm font-bold text-slate-600">
                  {user.count} actions
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Critical Events */}
      {summary.criticalEvents.length > 0 && (
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl overflow-hidden">
          <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-red-50 to-rose-50">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-red-500" />
              <h3 className="text-xl font-bold text-slate-900">
                Critical Events
              </h3>
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {summary.criticalEvents.slice(0, 5).map((event) => (
                <div
                  key={event._id}
                  className="flex items-start justify-between p-4 bg-gradient-to-r from-red-50 to-rose-50 rounded-xl border border-red-200"
                >
                  <div>
                    <div className="text-sm font-bold text-red-900">
                      {event.description}
                    </div>
                    <div className="text-xs font-semibold text-red-600 mt-1">
                      {event.userName} -{" "}
                      {new Date(event.timestamp).toLocaleString()}
                    </div>
                  </div>
                  <span className="inline-flex px-3 py-1 text-xs font-bold rounded-xl bg-gradient-to-r from-red-300 to-rose-400 text-white shadow-md">
                    {event.metadata.severity}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// User Activity Tab Component
const UserActivityTab: React.FC = () => {
  return (
    <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-16">
      <div className="text-center">
        <div className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-2xl p-8 shadow-lg inline-block mb-6">
          <Users className="w-16 h-16 text-white" />
        </div>
        <p className="text-slate-900 text-xl font-bold mb-2">
          User Activity Tracking
        </p>
        <p className="text-slate-600 font-semibold">
          Coming soon - detailed user activity analysis and reports
        </p>
      </div>
    </div>
  );
};

// Security Events Tab Component
const SecurityEventsTab: React.FC = () => {
  return (
    <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-16">
      <div className="text-center">
        <div className="bg-gradient-to-br from-purple-300 to-indigo-400 rounded-2xl p-8 shadow-lg inline-block mb-6">
          <Shield className="w-16 h-16 text-white" />
        </div>
        <p className="text-slate-900 text-xl font-bold mb-2">
          Security Events Analysis
        </p>
        <p className="text-slate-600 font-semibold">
          Coming soon - advanced security event monitoring and alerts
        </p>
      </div>
    </div>
  );
};

// Log Details Modal Component
const LogDetailsModal: React.FC<{
  log: AuditLog;
  onClose: () => void;
  getSeverityColor: (severity: string) => string;
  getCategoryColor: (category: string) => string;
  formatTimestamp: (timestamp: string) => { date: string; time: string };
}> = ({
  log,
  onClose,
  getSeverityColor,
  getCategoryColor,
  formatTimestamp,
}) => {
  const timestamp = formatTimestamp(log.timestamp);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-blue-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-purple-300 to-indigo-400 rounded-2xl p-3 shadow-lg">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">
                Audit Log Details
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 bg-white border-2 border-slate-200 rounded-xl text-slate-400 hover:text-slate-600 hover:shadow-lg hover:scale-110 transition-all duration-300"
            >
              <XCircle className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="p-4 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl">
                <label className="block text-sm font-bold text-slate-900 mb-2">
                  Timestamp
                </label>
                <div className="text-sm font-semibold text-slate-700">
                  {timestamp.date} at {timestamp.time}
                </div>
              </div>

              <div className="p-4 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl">
                <label className="block text-sm font-bold text-slate-900 mb-2">
                  User
                </label>
                <div>
                  <div className="text-sm font-bold text-slate-900">
                    {log.userName}
                  </div>
                  <div className="text-sm font-semibold text-slate-600">
                    {log.userEmail}
                  </div>
                </div>
              </div>

              <div className="p-4 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl">
                <label className="block text-sm font-bold text-slate-900 mb-2">
                  Action
                </label>
                <div className="text-sm font-semibold text-slate-700">
                  {log.action.replace(/_/g, " ")}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="p-4 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl">
                <label className="block text-sm font-bold text-slate-900 mb-2">
                  Category
                </label>
                <span
                  className={`inline-flex px-3 py-1 text-xs font-bold rounded-xl shadow-md ${getCategoryColor(
                    log.metadata.category
                  )}`}
                >
                  {log.metadata.category}
                </span>
              </div>

              <div className="p-4 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl">
                <label className="block text-sm font-bold text-slate-900 mb-2">
                  Severity
                </label>
                <span
                  className={`inline-flex px-3 py-1 text-xs font-bold rounded-xl shadow-md ${getSeverityColor(
                    log.metadata.severity
                  )}`}
                >
                  {log.metadata.severity}
                </span>
              </div>

              <div className="p-4 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl">
                <label className="block text-sm font-bold text-slate-900 mb-2">
                  Status
                </label>
                <div className="flex items-center gap-2">
                  {log.success ? (
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-500" />
                  )}
                  <span
                    className={`font-bold ${
                      log.success ? "text-emerald-700" : "text-red-700"
                    }`}
                  >
                    {log.success ? "Success" : "Failed"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="p-4 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl">
            <label className="block text-sm font-bold text-slate-900 mb-2">
              Description
            </label>
            <div className="text-sm font-semibold text-slate-700">
              {log.description}
            </div>
          </div>

          {/* Resource Info */}
          {(log.resourceType || log.resourceName) && (
            <div className="p-4 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl">
              <label className="block text-sm font-bold text-slate-900 mb-2">
                Resource
              </label>
              <div>
                <div className="text-sm font-bold text-slate-900">
                  {log.resourceType}
                </div>
                {log.resourceName && (
                  <div className="text-sm font-semibold text-slate-600">
                    {log.resourceName}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Target User */}
          {log.targetUserName && (
            <div className="p-4 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl">
              <label className="block text-sm font-bold text-slate-900 mb-2">
                Target User
              </label>
              <div className="text-sm font-semibold text-slate-700">
                {log.targetUserName}
              </div>
            </div>
          )}

          {/* Error Message */}
          {log.errorMessage && (
            <div className="p-4 bg-gradient-to-r from-red-50 to-rose-50 rounded-xl border border-red-200">
              <label className="block text-sm font-bold text-red-900 mb-2">
                Error Message
              </label>
              <div className="text-sm font-semibold text-red-700">
                {log.errorMessage}
              </div>
            </div>
          )}

          {/* Metadata */}
          <div className="p-4 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl">
            <label className="block text-sm font-bold text-slate-900 mb-3">
              Metadata
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              {log.metadata.ipAddress && (
                <div className="p-3 bg-white rounded-xl">
                  <span className="font-bold text-slate-900">IP Address:</span>{" "}
                  <span className="font-semibold text-slate-600">
                    {log.metadata.ipAddress}
                  </span>
                </div>
              )}
              {log.metadata.userAgent && (
                <div className="p-3 bg-white rounded-xl">
                  <span className="font-bold text-slate-900">User Agent:</span>{" "}
                  <span className="font-semibold text-slate-600">
                    {log.metadata.userAgent}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Old/New Values */}
          {(log.oldValues || log.newValues) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {log.oldValues && (
                <div className="p-4 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl">
                  <label className="block text-sm font-bold text-slate-900 mb-2">
                    Previous Values
                  </label>
                  <pre className="p-3 bg-white rounded-xl text-xs font-mono text-slate-700 overflow-x-auto border border-slate-200">
                    {JSON.stringify(log.oldValues, null, 2)}
                  </pre>
                </div>
              )}
              {log.newValues && (
                <div className="p-4 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl">
                  <label className="block text-sm font-bold text-slate-900 mb-2">
                    New Values
                  </label>
                  <pre className="p-3 bg-white rounded-xl text-xs font-mono text-slate-700 overflow-x-auto border border-slate-200">
                    {JSON.stringify(log.newValues, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-slate-200 bg-gradient-to-r from-slate-50 to-blue-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-3 bg-white border-2 border-slate-200 rounded-xl font-bold text-slate-700 hover:shadow-lg hover:scale-105 transition-all duration-300"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuditLogsPage;
